import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { lockScroll } from '@/lib/scroll-lock';
import { Button } from './button';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const openDialogs: object[] = [];

function isTopDialog(token: object): boolean {
  return openDialogs[openDialogs.length - 1] === token;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (node) => !node.hasAttribute('aria-hidden') && node.closest('[role="dialog"]') === root,
  );
}

function focusLeftThePage(panel: HTMLElement): boolean {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  if (panel.contains(active)) return false;
  return Boolean(document.getElementById('root')?.contains(active));
}

/**
 * Modal dialog. Focus moves in on open and returns on close, and Escape always
 * closes — a busy desk should never be trapped inside a dialog.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide,
  dismissable = true,
  icon: Icon,
  dirty = false,
  sheetOnPhone = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  dismissable?: boolean;
  icon?: LucideIcon;
  dirty?: boolean;
  sheetOnPhone?: boolean;
}) {
  const common = useTranslation('common');
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const token = useRef({}).current;
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // Callers write `onClose={() => setEditing(null)}`, so `onClose` is a fresh
  // function on every render of the parent. Held in a ref and kept out of the
  // dependencies below: with it in there, typing one character re-rendered the
  // parent, tore the effect down, and the cleanup's "restore focus" moved the
  // caret out of the field being typed into — one character per click.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const requestClose = () => {
    if (dirty) setConfirmingDiscard(true);
    else onClose();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    openDialogs.push(token);
    const onKey = (event: KeyboardEvent) => {
      if (!isTopDialog(token) || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        requestCloseRef.current();
        return;
      }
      const panel = panelRef.current;
      if (event.key === 'Tab' && panel && focusLeftThePage(panel)) {
        event.preventDefault();
        (focusables(panel)[0] ?? panel).focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    const timer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      (panel.querySelector<HTMLElement>('input, select, textarea, button') ?? panel).focus();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      const index = openDialogs.indexOf(token);
      if (index >= 0) openDialogs.splice(index, 1);
      unlock();
      clearTimeout(timer);
      (previouslyFocused.current as HTMLElement | null)?.focus?.();
    };
  }, [open, token]);

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || event.defaultPrevented || !isTopDialog(token) || !panelRef.current) return;
    const nodes = focusables(panelRef.current);
    if (nodes.length === 0) {
      event.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <>
    <div
      data-overlay
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/50 backdrop-blur-[3px] animate-in fade-in-0 duration-200 no-print dark:bg-black/60',
        sheetOnPhone ? 'px-0 pb-0 pt-6 sm:p-6' : 'p-4 sm:p-6',
      )}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={cn(
          'outline-none',
          'w-full rounded-2xl border border-border/70 bg-card shadow-2xl shadow-black/20 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200 ease-out',
          wide ? 'max-w-4xl' : 'max-w-lg',
          sheetOnPhone ? 'mt-auto max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0 sm:my-auto' : 'my-auto',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            {Icon && (
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" aria-hidden />
              </span>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight">{title}</h2>
              {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
            </div>
          </div>
          {dismissable && (
            <Button variant="ghost" size="icon" onClick={requestClose} aria-label={common.t('action.close')}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className={cn('relative max-h-[65vh] overflow-y-auto px-5 py-4', sheetOnPhone && 'max-sm:max-h-[calc(100dvh-11rem)]')}>{children}</div>
        {footer && <div className={cn('flex flex-wrap justify-end gap-2 rounded-b-2xl border-t bg-muted/30 px-5 py-3', sheetOnPhone && 'max-sm:rounded-b-none')}>{footer}</div>}
      </div>
    </div>
    {confirmingDiscard && (
      <Dialog
        open
        onClose={() => setConfirmingDiscard(false)}
        title={common.t('dialog.discard_title')}
        description={common.t('dialog.discard_body')}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmingDiscard(false)}>
              {common.t('form.keep_editing')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDiscard(false);
                onCloseRef.current();
              }}
            >
              {common.t('dialog.discard')}
            </Button>
          </>
        }
      >
        <span className="sr-only">{common.t('dialog.discard_body')}</span>
      </Dialog>
    )}
    </>
  );
}
